import { HttpClient } from './http-client';
import {
  DelegationDto,
  CreateDelegationRequest,
  UpdateDelegationRequest,
  DelegationListResponse,
  ActiveDelegationDto
} from '../types';

export class DelegationService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Get all delegations for the current user
   */
  async getMyDelegations(
    divisionId: string,
    activeOnly?: boolean,
    pageNumber: number = 1,
    pageSize: number = 50
  ): Promise<DelegationListResponse> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (activeOnly !== undefined) {
      params.append('activeOnly', activeOnly.toString());
    }

    return this.httpClient.get<DelegationListResponse>(
      `/api/v1/divisions/${divisionId}/delegations/me?${params.toString()}`
    );
  }

  /**
   * Get delegations given by the current user
   */
  async getMyDelegationsGiven(
    divisionId: string,
    activeOnly?: boolean,
    pageNumber: number = 1,
    pageSize: number = 50
  ): Promise<DelegationListResponse> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (activeOnly !== undefined) {
      params.append('activeOnly', activeOnly.toString());
    }

    return this.httpClient.get<DelegationListResponse>(
      `/api/v1/divisions/${divisionId}/delegations/me/given?${params.toString()}`
    );
  }

  /**
   * Get delegations received by the current user
   */
  async getMyDelegationsReceived(
    divisionId: string,
    activeOnly?: boolean,
    pageNumber: number = 1,
    pageSize: number = 50
  ): Promise<DelegationListResponse> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (activeOnly !== undefined) {
      params.append('activeOnly', activeOnly.toString());
    }

    return this.httpClient.get<DelegationListResponse>(
      `/api/v1/divisions/${divisionId}/delegations/me/received?${params.toString()}`
    );
  }

  /**
   * Get current active delegation for the user (if any)
   */
  async getMyActiveDelegation(divisionId: string): Promise<ActiveDelegationDto | null> {
    try {
      return await this.httpClient.get<ActiveDelegationDto>(
        `/api/v1/divisions/${divisionId}/delegations/me/active`
      );
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new delegation
   */
  async createDelegation(
    divisionId: string,
    request: CreateDelegationRequest
  ): Promise<DelegationDto> {
    return this.httpClient.post<DelegationDto>(
      `/api/v1/divisions/${divisionId}/delegations`,
      request
    );
  }

  /**
   * Get a specific delegation by ID
   */
  async getDelegationById(
    divisionId: string,
    delegationId: string
  ): Promise<DelegationDto> {
    return this.httpClient.get<DelegationDto>(
      `/api/v1/divisions/${divisionId}/delegations/${delegationId}`
    );
  }

  /**
   * Update an existing delegation
   */
  async updateDelegation(
    divisionId: string,
    delegationId: string,
    request: UpdateDelegationRequest
  ): Promise<DelegationDto> {
    return this.httpClient.put<DelegationDto>(
      `/api/v1/divisions/${divisionId}/delegations/${delegationId}`,
      request
    );
  }

  /**
   * Deactivate a delegation
   */
  async deactivateDelegation(divisionId: string, delegationId: string): Promise<void> {
    await this.httpClient.post(
      `/api/v1/divisions/${divisionId}/delegations/${delegationId}/deactivate`,
      {}
    );
  }

  /**
   * Delete a delegation
   */
  async deleteDelegation(divisionId: string, delegationId: string): Promise<void> {
    await this.httpClient.delete(
      `/api/v1/divisions/${divisionId}/delegations/${delegationId}`
    );
  }

  /**
   * Get all active delegations for a division (Admin only)
   */
  async getActiveDelegations(divisionId: string): Promise<ActiveDelegationDto[]> {
    return this.httpClient.get<ActiveDelegationDto[]>(
      `/api/v1/divisions/${divisionId}/delegations/active`
    );
  }

  /**
   * Get delegations for a specific user (Admin only)
   */
  async getUserDelegations(
    divisionId: string,
    userId: string,
    activeOnly?: boolean,
    pageNumber: number = 1,
    pageSize: number = 50
  ): Promise<DelegationListResponse> {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    
    if (activeOnly !== undefined) {
      params.append('activeOnly', activeOnly.toString());
    }

    return this.httpClient.get<DelegationListResponse>(
      `/api/v1/divisions/${divisionId}/delegations/users/${userId}?${params.toString()}`
    );
  }
}
